'use client';

import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Avatar } from '@pickleballinc/ui/components/ui/avatar';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { ImageWrapper } from '@/modules/players/v2/ImageWrapper';
import { WORLD_PICKLEBALL_RANKINGS } from '@/modules/rankings/constants';
import { TWO_DASHES_PLACEHOLDER } from '@/modules/rankings/ppr/constants';
import { DivisionTypeButtonsWrapper } from '@/modules/rankings/ppr/NavigationButtons';
import { ProfileCard } from '@/modules/rankings/ppr/ProfileCard';
import { InfoItem, RankingPlayerProfileData } from '@/modules/rankings/ppr/types';

interface RankingProfileHeaderProps {
  playerProfileData: RankingPlayerProfileData;
  isOwnProfile?: boolean;
}

function HiddenValue() {
  return <FontAwesomeIcon icon={faEyeSlash} />;
}

function InfoGrid({ items, mobile = false }: { items: InfoItem[]; mobile?: boolean }) {
  return (
    <div
      className={clsx(
        'bg-gray-100 grid',
        mobile
          ? 'py-4 w-full rounded-lg grid-cols-2 gap-y-4'
          : 'grid-cols-3 rounded-2xl overflow-hidden min-h-[78px]'
      )}
    >
      {items.map((item, index) => {
        const isLastColumn = mobile ? index % 2 === 1 : index % 3 === 2;

        return (
          <div
            key={item.label}
            className={clsx(
              'relative flex flex-col items-center justify-center text-center',
              mobile ? 'px-3' : 'px-6 py-4'
            )}
          >
            {!isLastColumn ? (
              <span
                className={clsx(
                  'absolute right-0 top-1/2 w-px -translate-y-1/2 bg-gray-300',
                  mobile ? 'h-9' : 'h-[58px]'
                )}
              />
            ) : null}

            <p
              className={clsx(
                'text-gray-500 font-semibold',
                mobile ? 'text-sm mb-1' : 'text-[15px] leading-6 mb-1'
              )}
            >
              {item.label}
            </p>

            <p
              className={clsx(
                'text-gray-900 font-semibold',
                mobile ? 'text-sm px-1' : 'text-base leading-7'
              )}
            >
              {item.value}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export function RankingProfileHeader({
  playerProfileData,
  isOwnProfile,
}: RankingProfileHeaderProps) {
  const searchParams = useSearchParams();
  const [showStickyProfile, setShowStickyProfile] = useState(false);
  const inlineNavRef = useRef<HTMLDivElement>(null);

  const pageType = searchParams?.get('type') || 'player-profile';
  const isRankHistory = pageType === 'rank-history';
  const toggleHref = isRankHistory ? '?type=player-profile' : '?type=rank-history';
  const toggleLabel = isRankHistory ? 'View Player Profile' : 'View Rank History';

  const handleIntersection = useCallback(([entry]: IntersectionObserverEntry[]) => {
    if (!entry) return;
    const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    setShowStickyProfile(scrolledPast);
  }, []);

  useEffect(() => {
    const node = inlineNavRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0,
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection]);

  const hasHeaderImage = !!playerProfileData.headerImage;

  const mobileInfoItems: InfoItem[] = [
    { label: 'Gender', value: playerProfileData.gender },
    { label: 'Plays', value: playerProfileData.plays },
    {
      label: 'Location',
      value:
        playerProfileData.location === TWO_DASHES_PLACEHOLDER ? (
          <HiddenValue />
        ) : (
          playerProfileData.location
        ),
    },
    { label: 'Turned Pro', value: playerProfileData.turnedPro },
    {
      label: 'Age',
      value: playerProfileData.age === '0' ? <HiddenValue /> : `${playerProfileData.age} yrs`,
    },
    {
      label: 'Height',
      value: playerProfileData.heightFeetAndInches,
    },
  ];

  const desktopTopItems: InfoItem[] = [
    { label: 'Gender', value: playerProfileData.gender },
    { label: 'Plays', value: playerProfileData.plays },
    {
      label: 'Location',
      value:
        playerProfileData.location === TWO_DASHES_PLACEHOLDER ? (
          <HiddenValue />
        ) : (
          playerProfileData.location
        ),
    },
  ];

  const desktopBottomItems: InfoItem[] = [
    { label: 'Turned Pro', value: playerProfileData.turnedPro },
    {
      label: 'Age',
      value: playerProfileData.age === '0' ? <HiddenValue /> : `${playerProfileData.age} yrs`,
    },
    {
      label: 'Height',
      value: playerProfileData.heightFeetAndInches,
    },
  ];

  const avatarSrc = playerProfileData.profileImage
    ? getCDNURL(playerProfileData.profileImage, 190, 190, 0, true)
    : undefined;

  return (
    <div className='max-w-7xl mx-auto px-4 xl:px-0'>
      <h2 className='text-xl md:text-2xl font-bold text-gray-800 m-2 pt-2'>
        {isRankHistory
          ? `${isOwnProfile ? 'My ' : ''}${WORLD_PICKLEBALL_RANKINGS} History`
          : `${isOwnProfile ? 'My ' : ''}${WORLD_PICKLEBALL_RANKINGS}`}
      </h2>

      {showStickyProfile ? (
        <div className='fixed top-0 left-0 right-0 z-50 bg-white shadow-sm transition-transform duration-300 translate-y-0'>
          <div className='md:max-w-7xl mx-auto w-full px-4 md:px-0'>
            <ProfileCard playerProfileData={playerProfileData} />
          </div>
          {/* Stacked in flow under the card so the gap holds however tall the card renders. */}
          <div className='px-4 pt-2 pb-2'>
            <div className='max-w-7xl mx-auto'>
              <DivisionTypeButtonsWrapper gender={playerProfileData.gender} />
            </div>
          </div>
        </div>
      ) : null}

      <div className='overflow-x-hidden bg-white mx-auto'>
        <div className='bg-white rounded-xl border border-gray-200 flex flex-col md:flex-row w-full overflow-hidden mb-4'>
          <div className='w-full h-[200px] flex-shrink-0 relative bg-white flex items-center justify-center md:hidden'>
            {hasHeaderImage ? (
              <div className='relative size-full py-4'>
                <ImageWrapper
                  src={getCDNURL(playerProfileData.headerImage, 497, 438)}
                  className='object-contain'
                  fill
                  fallbackSrc={getCDNURL(
                    `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/profile-image-pro.svg`,
                    497,
                    438
                  )}
                  alt={`${playerProfileData.fullName} Header Image`}
                />
              </div>
            ) : (
              <Avatar
                name={playerProfileData.fullName}
                src={avatarSrc}
                size='5xl'
                className='border border-gray-200 bg-gray-100'
                textClassName='text-gray-500'
              />
            )}
          </div>

          <div className='hidden md:flex md:w-[307px] md:h-[330px] md:my-auto flex-shrink-0 relative bg-white items-center justify-center'>
            {hasHeaderImage ? (
              <div className='relative size-full pt-8'>
                <ImageWrapper
                  src={getCDNURL(playerProfileData.headerImage, 497, 438)}
                  className='object-contain'
                  fill
                  fallbackSrc={getCDNURL(
                    `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/profile-image-pro.svg`,
                    497,
                    438
                  )}
                  alt={`${playerProfileData.fullName} Header Image`}
                />
              </div>
            ) : (
              <Avatar
                name={playerProfileData.fullName}
                src={avatarSrc}
                className='size-48 border border-gray-200 bg-gray-100'
                textClassName='text-6xl text-gray-500'
              />
            )}
          </div>

          <div className='flex-1 p-4 md:px-6 md:py-5 flex flex-col justify-between gap-4'>
            <div className='flex flex-col md:flex-row justify-between items-center md:items-start gap-4'>
              <div className='w-full'>
                <h1 className='text-lg md:text-3xl md:leading-9 font-semibold text-gray-900 text-center md:text-left'>
                  {playerProfileData.fullName}
                </h1>

                <div className='flex items-center justify-center md:justify-start gap-2 mt-1'>
                  {playerProfileData.countryFlag !== '' &&
                    playerProfileData.countryAbbreviation !== '' && (
                      <>
                        <div className='w-6 h-6 rounded-full overflow-hidden border border-gray-100 flex items-center justify-center'>
                          <img
                            src={playerProfileData.countryFlag}
                            alt={`${playerProfileData.fullCountry} Flag`}
                            className='size-5'
                          />
                        </div>
                        <span className='text-sm text-gray-700 uppercase'>
                          {playerProfileData.countryAbbreviation}
                        </span>
                      </>
                    )}
                  {playerProfileData.countryFlag !== '' &&
                  playerProfileData.countryAbbreviation !== '' &&
                  playerProfileData.typeOfPlayer ? (
                    <span className='text-sm text-gray-700'>|</span>
                  ) : null}
                  {playerProfileData.typeOfPlayer ? (
                    <span className='text-sm text-gray-700 uppercase'>
                      {playerProfileData.typeOfPlayer}
                    </span>
                  ) : null}
                </div>
              </div>

              <CustomLink href={toggleHref} className='w-full sm:w-auto md:min-w-[170px]'>
                <button className='px-4 py-2.5 h-10 w-full whitespace-nowrap flex items-center justify-center border border-brand-800 text-brand-800 text-sm font-semibold rounded-xl bg-brand-25 hover:bg-brand-50'>
                  {toggleLabel}
                </button>
              </CustomLink>
            </div>

            <div className='flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-0'>
              <div className='flex flex-col items-center md:items-start'>
                <div className='flex items-center gap-3 py-1'>
                  <span className='text-sm md:text-base text-gray-500 font-semibold'>
                    {WORLD_PICKLEBALL_RANKINGS}
                  </span>
                  <span className='text-gray-900 text-lg leading-none md:text-2xl font-semibold'>
                    {`#${playerProfileData.pprRank}`}
                  </span>
                </div>
              </div>

              <div className='hidden md:block mx-4 h-[62px] w-px bg-gray-300 shrink-0' />

              <div className='flex flex-col md:items-start'>
                <div className='flex items-center gap-3 py-1'>
                  <span className='text-sm md:text-base text-gray-500 font-semibold'>
                    {`${WORLD_PICKLEBALL_RANKINGS} Points`}
                  </span>
                  <span className='text-gray-900 text-lg leading-none md:text-2xl font-semibold'>
                    {playerProfileData.pprPointsTotal}
                  </span>
                </div>
              </div>
            </div>

            <div className='block md:hidden w-full'>
              <InfoGrid items={mobileInfoItems} mobile />
            </div>

            <div className='hidden md:flex flex-col gap-3'>
              <InfoGrid items={desktopTopItems} />
              <InfoGrid items={desktopBottomItems} />
            </div>
          </div>
        </div>
      </div>

      <div ref={inlineNavRef} className='max-w-7xl mx-auto py-2'>
        <DivisionTypeButtonsWrapper gender={playerProfileData.gender} />
      </div>
    </div>
  );
}
